import React, { useState, useEffect } from "react";
import { 
  collection, 
  getDocs, 
  getDoc,
  addDoc, 
  doc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  runTransaction 
} from "firebase/firestore";


import { db } from "../firebaseConfig";
import { addJobTotal } from "../utils/addJobTotal";
import { useNavigate } from "react-router-dom";
import "./ProposalsPPD.css";
import { sendPPDProposalEmail, describeLastProposalEmail } from "./PPDProposalEmail";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import html2pdf from "html2pdf.js";
import { serverTimestamp, increment } from "firebase/firestore";


const getServiceCode = (value = "") => String(value || "").trim().split(/\s+-\s+|\s+/)[0].toUpperCase();
const readPropertyPricing = (property = {}, serviceType = "") => {
  const code = getServiceCode(serviceType);
  const overrides = property?.servicePricingOverrides || {};
  const override = overrides[code] || overrides[serviceType] || {};
  const legacy = property?.serviceTypeTotals || property?.serviceTotals || {};
  return {
    amount: override.amount ?? override.total ?? legacy[code] ?? legacy[serviceType] ?? "",
    initialAmount: override.initialAmount ?? "",
    feeSchedule: override.feeSchedule || "",
  };
};
const resolveServicePricing = (property = {}, template = {}, serviceType = "") => {
  const override = readPropertyPricing(property, serviceType);
  return {
    amount: override.amount !== "" && override.amount != null ? override.amount : (template?.total ?? ""),
    initialAmount: override.initialAmount !== "" && override.initialAmount != null ? override.initialAmount : (template?.initialAmount ?? ""),
    feeSchedule: override.feeSchedule || template?.feeSchedule || "Monthly",
  };
};

const ProposalsPPD = () => {
  const [proposals, setProposals] = useState([]);
  const [properties, setProperties] = useState([]);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [proposalDropdowns, setProposalDropdowns] = useState([]);
const [termsOptions, setTermsOptions] = useState([]);
const [warrantyOptions, setWarrantyOptions] = useState([]);
const [activeSubOptions, setActiveSubOptions] = useState(null);
const [serviceTypeTemplates, setServiceTypeTemplates] = useState([]);

const [newProposal, setNewProposal] = useState({
  companyName: "",
  companyAddress: "",
  companyCityStateZip: "",
  siteName: "",
  management: "",
  managerName: "",
  managerEmail: "",
  clientEmail: "",
  accountingEmail: "",
  address: "",
  cityStateZip: "",
  serviceType: "",
  scope: "",
  dropdownValues: {},
  jobDescription: "",
  price: "",
    proposalTotal: "",
  initialAmount: "",
  feeSchedule: "Monthly",
  submittedDate: "",
  submittedBy: "",
  images: [],
  terms: "",
  warrantyName: "",
  warranty: "",
});
  const [proposalID, setProposalID] = useState(3000);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pdfDownloadUrl, setPdfDownloadUrl] = useState(""); // Add state for PDF download URL
  const navigate = useNavigate();
  const [sendingProposalId, setSendingProposalId] = useState(null);
  const goToApproved = () => {
    navigate("/PPDApprovedProposals");
  };
  
  const goToCompleted = () => {
    navigate("/PPDCompletedProposals");
  };

  const goToSentProposals = () => {
    navigate("/PPDSentProposals");
  };

  const goToProposals = () => {
    navigate("/ProposalsPPD");
  };


  const [sortOrder, setSortOrder] = useState("asc");

  // Helpers
  const formatInitials = (nameOrFirstLast) => {
    if (!nameOrFirstLast) return "";
    // Accept either "First Last" or already-initials and normalize to "F L"
    const parts = String(nameOrFirstLast).trim().split(/\s+/);
    const first = parts[0]?.[0] || "";
    const last  = parts[1]?.[0] || "";
    return [first, last].filter(Boolean).join(" ");
  };
  
  const formatDateMMDDYYYY = (isoOrYmd) => {
    if (!isoOrYmd) return "";
    // Handles "YYYY-MM-DD" from <input type="date"> or full ISO strings
    const d = new Date(isoOrYmd);
    if (Number.isNaN(d.getTime())) return isoOrYmd; // fallback
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${mm}-${dd}-${yyyy}`;
  };
// ✅ Build a safe PDF/file title for Chrome print "Save as"
const buildPdfTitle = (p) => {
  const site = (p?.siteName || "Site").trim();
  const scope = (p?.scope || "Scope").trim();

  // proposals - siteName - scope
  const raw = `${site} - ${scope}`;

  // sanitize forbidden filename characters + collapse spaces
  return raw
    .replace(/[/\\?%*:|"<>]/g, "")
    .replace(/\s+/g, " ")
    .trim();
};

// ✅ Images used by the displayed proposal, print PDF, saved Storage PDF, and email PDF.
// Handles both the newer imageURLs field and older/edited images arrays.
const getProposalImageUrls = (proposal = {}) => {
  const rawImages = [
    ...(Array.isArray(proposal.imageURLs) ? proposal.imageURLs : []),
    ...(Array.isArray(proposal.images) ? proposal.images : []),
  ];

  const urls = rawImages
    .map((item) => {
      if (typeof item === "string") return item;
      if (item && typeof item === "object") {
        return item.fileURL || item.fileUrl || item.downloadURL || item.url || item.src || "";
      }
      return "";
    })
    .filter(Boolean);

  return Array.from(new Set(urls));
};


  const logoOptions = [
    { name: "Priority Pest Defense", url: "https://firebasestorage.googleapis.com/v0/b/priority-backend.firebasestorage.app/o/logo-images%2Fprioritypestlogogreyblue.png?alt=media&token=da273ab0-3054-4675-bb96-3a3a4a242434", alignment: "center" }
  ];

  const PPD_NAME = "Priority Pest Defense LLC";

const getServiceScope = (serviceType = "") => {
  const value = String(serviceType || "").trim();
  if (!value) return "";
  const separatorIndex = value.indexOf(" - ");
  return separatorIndex >= 0 ? value.slice(separatorIndex + 3).trim() : value;
};

const isLegacyServiceDropdown = (dropdown = {}) => {
  const label = String(dropdown.label || "").trim().toLowerCase();
  const fieldName = String(dropdown.fieldName || "").trim().toLowerCase();
  return label === "service" || fieldName === "service";
};

const serviceTypes = [
  "Action Items",
  "Inspection",
  "Meeting",
  "Proposal",
  "BED - Bed Bug Treatment",
  "HTT - Home Termite Treatment",
  "MPC - Monthly Pest Control",
  "QPC - Quarterly Pest Control",
  "RBS - Rodent Bait Station Service",
  "SEN - Termite Monitoring Station Service",
  "STT - Settlement Termite Treatment",
  "WDI - Wood Destroying Insect Inspection",
  "WLW - Wildlife Removal/Exclusion",
  "WPC - Warranty Pest Control",
  "WYG - Warranty Yard Guard",
  "YG - Yard Guard",
];

  const [selectedLogo, setSelectedLogo] = useState(logoOptions[0]);

  const handleLogoChange = (e) => {
    const selected = logoOptions.find(option => option.name === e.target.value);
    setSelectedLogo(selected);
  };

 

  const handleWarrantyChange = (e) => {
    const name = e.target.value;
    const found = warrantyOptions.find(opt => opt.name === name);
    if (!found) return;
  
    let text = "";
  
    if (typeof found.content === "function") {
      text = found.content({
        companyName: newProposal.companyName || selectedProposal?.companyName || "",
        siteName: newProposal.siteName || selectedProposal?.siteName || ""
      });
    } else {
      text = found.content;
    }
  
    setNewProposal(prev => ({
      ...prev,
      warrantyName: name,
      warranty: text
    }));
  };
  
// ✅ Dynamic dropdown → (optional sub options) → append into jobDescription

const addToJobDescription = (field, value, label) => {
  setNewProposal(prev => {
    const sep = prev.jobDescription ? "\n\n" : "";
    return {
      ...prev,
      dropdownValues: { ...(prev.dropdownValues || {}), [field]: value },
      jobDescription:
        prev.jobDescription + sep + `((b))${String(label || "").toUpperCase()}\n${value}`
    };
  });
};

const handleServiceTypeChange = (e) => {
  const value = e.target.value;
  const matchedTemplate = serviceTypeTemplates.find((item) => item.serviceType === value);
  const property = properties.find((item) => item.propertyName === newProposal.siteName);
  const pricing = resolveServicePricing(property, matchedTemplate, value);
  setNewProposal((prev) => ({
    ...prev,
    serviceType: value,
    scope: getServiceScope(value),
    jobDescription: matchedTemplate?.content?.trim() || "",
    proposalTotal: pricing.amount,
    initialAmount: pricing.initialAmount,
    feeSchedule: pricing.feeSchedule,
  }));
};

const persistProposalPricingOverride = async (field, value) => {
  const property = properties.find((item) => item.propertyName === newProposal.siteName);
  if (!property?.id || !newProposal.serviceType) return;
  const code = getServiceCode(newProposal.serviceType);
  const current = readPropertyPricing(property, newProposal.serviceType);
  const next = { ...current, [field]: value === "" ? "" : (field === "feeSchedule" ? value : Number(value)) };
  await updateDoc(doc(db, "properties", property.id), { [`servicePricingOverrides.${code}`]: next });
  setProperties((prev) => prev.map((item) => item.id === property.id ? { ...item, servicePricingOverrides: { ...(item.servicePricingOverrides || {}), [code]: next } } : item));
};

const handleDropdownChange = (field, rawValue, label) => {
  if (!rawValue) return;

  let parsed;
  try {
    parsed = JSON.parse(rawValue);
  } catch (err) {
    console.warn("Dropdown value JSON parse failed:", err, rawValue);
    return;
  }

  // If no subOptions, add immediately
  if (!parsed?.subOptions || parsed.subOptions.length === 0) {
    const paragraph = parsed.value ?? parsed.label ?? "";
    addToJobDescription(field, paragraph, label);
    return;
  }
  

  // Has subOptions → show sub-option buttons
  setActiveSubOptions({
    field,
    label,
    options: parsed.subOptions
  });
};

  useEffect(() => {
    const fetchProposals = async () => {
      const proposalsRef = collection(db, "proposals");
    
      // ONLY pull proposals for PPD
      const q = query(
        proposalsRef,
        where("companyName", "==", "Priority Pest Defense LLC")
      );
    
      const querySnapshot = await getDocs(q);
    
      const proposalsData = querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(p => p.status === "Not Sent");
    
      setProposals(proposalsData);
    };
    
    const fetchProposalDropdowns = async () => {
      const snap = await getDocs(collection(db, "proposalsPPDDropdowns"));
      const data = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      setProposalDropdowns(data);
    };
    
    const fetchServiceTypeTemplates = async () => {
      const snap = await getDocs(collection(db, "proposalsPPDServiceTypes"));
      setServiceTypeTemplates(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    };
    
    const fetchProposalTerms = async () => {
      const snap = await getDocs(collection(db, "proposalsPPDTerms"));
      setTermsOptions(snap.docs.map(d => d.data()));
    };
    
    const fetchProposalWarranties = async () => {
      const snap = await getDocs(collection(db, "proposalsPPDWarranties"));
      setWarrantyOptions(snap.docs.map(d => d.data()));
    };
    
    const fetchProperties = async () => {
      const propertiesRef = collection(db, "properties");
      const querySnapshot = await getDocs(propertiesRef);
      const propertiesData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setProperties(propertiesData);
    };

    const fetchUsers = async () => {
      const usersRef = collection(db, "users");
      const querySnapshot = await getDocs(usersRef);
      const usersData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsers(usersData);
    };

    const fetchCompanies = async () => {
      const companiesRef = collection(db, "companies");
      const querySnapshot = await getDocs(companiesRef);
      const companiesData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCompanies(companiesData);
    };

    fetchProposals();
    fetchProperties();
    fetchUsers();
    fetchCompanies();
    fetchProposalDropdowns();
    fetchProposalTerms();
    fetchProposalWarranties();
    fetchServiceTypeTemplates();

  }, []);

  useEffect(() => {
    if (selectedProposal) {
      setNewProposal({
  companyName: selectedProposal.companyName || "",
  companyAddress: selectedProposal.companyAddress || "",
  companyCityStateZip: selectedProposal.companyCityStateZip || "",
  siteName: selectedProposal.siteName || "",
  management: selectedProposal.management || "",
  managerName: selectedProposal.managerName || "",
  address: selectedProposal.address || "",
  cityStateZip: selectedProposal.cityStateZip || "",
  serviceType: selectedProposal.serviceType || "",
  scope: selectedProposal.scope || "",
  dropdownValues: selectedProposal.dropdownValues || {},
  jobDescription: selectedProposal.jobDescription || "",
  price: selectedProposal.price || "",
        proposalTotal: selectedProposal.proposalTotal || selectedProposal.contractAmount || selectedProposal.estimatedTotal || "",
  initialAmount: selectedProposal.initialAmount || "",
  feeSchedule: selectedProposal.feeSchedule || "Monthly",
  submittedDate: selectedProposal.submittedDate || "",
  submittedBy: selectedProposal.submittedBy || "",
  images: selectedProposal.images || [],
  terms: selectedProposal.terms || "",
  warrantyName: selectedProposal.warrantyName || "",
  warranty: selectedProposal.warranty || "",
});
    }
  }, [selectedProposal]);

  const sortedProperties = properties.sort((a, b) => a.propertyName.localeCompare(b.propertyName));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewProposal((prev) => ({ ...prev, [name]: value }));
  };

  const handleCompanyNameChange = (e) => {
    const selectedCompany = companies.find(company => company.companyName === e.target.value);
    const matchedLogo = logoOptions.find(logo => logo.name === selectedCompany.companyName);
    setNewProposal((prev) => ({
      ...prev,
      companyName: selectedCompany.companyName,
      companyAddress: selectedCompany.companyAddress || "",
      companyCityStateZip: `${selectedCompany.companyCity}, ${selectedCompany.companyState} ${selectedCompany.companyZip}` || ""
    }));
    if (matchedLogo) {
      setSelectedLogo(matchedLogo);
    }
  };

  const handleSiteNameChange = (e) => {
    const selectedProperty = properties.find(property => property.propertyName === e.target.value);
    if (!selectedProperty) return;
    const template = serviceTypeTemplates.find((item) => item.serviceType === newProposal.serviceType);
    const pricing = resolveServicePricing(selectedProperty, template, newProposal.serviceType);
    setNewProposal((prev) => ({
      ...prev, siteName: selectedProperty.propertyName, management: selectedProperty.management || "",
      managerName: selectedProperty.managerName || "", managerEmail: selectedProperty.managerEmail || selectedProperty.managementEmail || "",
      clientEmail: selectedProperty.clientEmail || "", accountingEmail: selectedProperty.accountingEmail || "",
      address: selectedProperty.address || selectedProperty.managementAddress || "",
      cityStateZip: `${selectedProperty.city || selectedProperty.managementCity || ""}, ${selectedProperty.state || selectedProperty.managementState || ""} ${selectedProperty.zip || selectedProperty.managementZip || ""}`.trim(),
      proposalTotal: prev.serviceType ? pricing.amount : prev.proposalTotal,
      initialAmount: prev.serviceType ? pricing.initialAmount : prev.initialAmount,
      feeSchedule: prev.serviceType ? pricing.feeSchedule : prev.feeSchedule,
    }));
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setNewProposal(prevState => ({
      ...prevState,
      images: [...prevState.images, ...selectedFiles], // Append new files without removing URLs
    }));
  };
  

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { images, ...proposalData } = newProposal;
      const proposalDocRef = await addDoc(collection(db, "proposals"), {
        ...proposalData,
        status: "Not Sent",
        hasBeenSent: false
      });
      let imageURLs = [];
      if (images.length > 0) {
        const storage = getStorage();
        const uploadPromises = images.map(async (file) => {
          const storageRef = ref(storage, `proposals/${proposalDocRef.id}/${file.name}`);
          await uploadBytes(storageRef, file);
          return await getDownloadURL(storageRef);
        });
        imageURLs = await Promise.all(uploadPromises);
        await updateDoc(proposalDocRef, { imageURLs });
      }

      setProposals([...proposals, { id: proposalDocRef.id, ...proposalData, imageURLs }]);
      setProposalID(proposalID + 1);
      setNewProposal({
        companyName: "",
        companyAddress: "",
        companyCityStateZip: "",
        siteName: "",
        management: "",
        managerName: "",
        address: "",
        cityStateZip: "",
        serviceType: "",
        scope: "",
        jobDescription: "",
        price: "",
    proposalTotal: "",
  initialAmount: "",
        submittedDate: "",
        submittedBy: "",
        images: [],
        terms: "",
        warranty: "",
      });
      setIsEditing(false);
    } catch (error) {
      console.error("Error adding proposal:", error);
    }
  };

  const handleEditProposal = async () => {
    try {
      const proposalRef = doc(db, "proposals", selectedProposal.id);
  
      // Extract files from newProposal.images
      const files = newProposal.images.filter(img => img instanceof File);
      let imageURLs = selectedProposal.imageURLs || []; // Keep existing URLs
  
      if (files.length > 0) {
        const storage = getStorage();
        const uploadPromises = files.map(async (file) => {
          const storageRef = ref(storage, `proposals/${selectedProposal.id}/${file.name}`);
          await uploadBytes(storageRef, file);
          return await getDownloadURL(storageRef);
        });
  
        const uploadedImageURLs = await Promise.all(uploadPromises);
        imageURLs = [...imageURLs, ...uploadedImageURLs]; // Merge new and old URLs
      }
  
      // Update Firestore (store URLs, not files)
      await updateDoc(proposalRef, { 
        ...newProposal, 
        imageURLs, 
        images: imageURLs 
      });
  
      // Update UI
      setProposals(proposals.map(proposal => 
        proposal.id === selectedProposal.id ? { ...proposal, imageURLs, images: imageURLs } : proposal
      ));
  
      setSelectedProposal({ ...selectedProposal, imageURLs, images: imageURLs });
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating proposal:", error);
    }
  };
  

  const handleDeleteProposal = async () => {
    try {
      const proposalRef = doc(db, "proposals", selectedProposal.id);
      await deleteDoc(proposalRef);
      setProposals(proposals.filter(proposal => proposal.id !== selectedProposal.id));
      setSelectedProposal(null);
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error("Error deleting proposal:", error);
    }
  };

  const handleCancel = () => {
    setNewProposal({
      companyName: "",
      companyAddress: "",
      companyCityStateZip: "",
      siteName: "",
      management: "",
      managerName: "",
      address: "",
      cityStateZip: "",
      serviceType: "",
      scope: "",
      jobDescription: "",
      price: "",
    proposalTotal: "",
  initialAmount: "",
      submittedDate: "",
      submittedBy: "",
      images: [],
      terms: "",
      warranty: "",
    });
    setIsEditing(false);
  };

  const parseJobDescription = (description) => {
    if (!description) return null;
  
    return description.split("\n").map((line, index) => {
      const rawLine = line || "";
      const trimmed = rawLine.trim();
  
      if (trimmed === "") {
        return <br key={index} />;
      }
  
      if (/^\(\([bB]\)\)/.test(trimmed)) {
        return (
          <p key={index} className="bold">
            {trimmed.replace(/^\(\([bB]\)\)/, "").trim()}
          </p>
        );
      }
  
      if (/^\(\([iI]\)\)/.test(trimmed)) {
        return (
          <p key={index} className="italic">
            {trimmed.replace(/^\(\([iI]\)\)/, "").trim()}
          </p>
        );
      }
  
      if (/^\(\([rR]\)\)/.test(trimmed)) {
        return (
          <p key={index}>
            {trimmed.replace(/^\(\([rR]\)\)/, "").trim()}
          </p>
        );
      }
  
      if (trimmed.startsWith("((-))")) {
        return (
          <li key={index} className="bullet">
            {trimmed.replace("((-))", "").trim()}
          </li>
        );
      }
  
      return <p key={index}>• {rawLine}</p>;
    });
  };

  const handleEditClick = () => {
    if (selectedProposal) {
      setNewProposal({
        companyName: selectedProposal.companyName || "",
        companyAddress: selectedProposal.companyAddress || "",
        companyCityStateZip: selectedProposal.companyCityStateZip || "",
        siteName: selectedProposal.siteName || "",
        management: selectedProposal.management || "",
        managerName: selectedProposal.managerName || "",
        managerEmail: selectedProposal.managerEmail || "",
        clientEmail: selectedProposal.clientEmail || "",
        accountingEmail: selectedProposal.accountingEmail || "",
        address: selectedProposal.address || "",
        cityStateZip: selectedProposal.cityStateZip || "",
        serviceType: selectedProposal.serviceType || "",
        scope: selectedProposal.scope || "",
        jobDescription: selectedProposal.jobDescription || "",
        price: selectedProposal.price || "",
        proposalTotal: selectedProposal.proposalTotal || selectedProposal.contractAmount || selectedProposal.estimatedTotal || "",
  initialAmount: selectedProposal.initialAmount || "",
  feeSchedule: selectedProposal.feeSchedule || "Monthly",
        submittedDate: selectedProposal.submittedDate || "",
        submittedBy: selectedProposal.submittedBy || "",
        images: selectedProposal.images || [],
        terms: selectedProposal.terms || "",
        warranty: selectedProposal.warranty || "",
      });
      setIsEditing(true);
    }
  };
  

  const buildPrintDocument = () => {
    const printable = document.getElementById("printableArea");
    if (!printable) return null;

    const title = buildPdfTitle(selectedProposal);
    const content = printable.outerHTML;
    const printCss = `
    <style>
      /* Paper */
      @page {
        size: Letter;
        margin: 0.5in;
      }
  
      html, body {
        margin: 0;
        padding: 0;
        height: 100%;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        color: #111;
  
        /* ✅ CHANGE: new PDF font */
        font: 11.5pt/1.45 Arial, Helvetica, sans-serif;
      }
  
      /* Hide app controls in print */
      @media print { .no-print { display: none !important; } }
  
      /* ✅ CHANGE: remove borders + add shadow look */
      .page-border {
        border: none !important;
        border-radius: 10px !important;
        padding: 18px !important;
        background: #fff !important;
        box-shadow: 0 10px 24px rgba(0,0,0,0.14) !important;
      }
  
      /* ✅ CHANGE: top row layout in print */
      @media print {
        .pdf-top-row {
          display: flex !important;
          gap: 14px !important;
          align-items: flex-start !important;
          margin: 0 0 14px 0 !important;
        }
        .pdf-header {
          display: flex !important;
          flex-direction: column !important;
          align-items: flex-end !important; /* ✅ logo + company info to the right */
          gap: 8px !important;
        }
      }
  
      /* ✅ CHANGE: logo top-right (print only) */
      .companylogo-container {
        text-align: right !important;
        margin: 0 !important;
        width: 100% !important;
      }
      .companylogo-container img.resizable-logo {
        width: 140px !important;
        max-width: 140px !important;
        height: auto !important;
        display: inline-block !important;
      }
  
      /* ✅ CHANGE: remove borders on all section cards + add shadow */
      .companyinfo-container,
      .property-container,
      .scope-container,
      .description-container,
      .total-container,
      .image-container,
      .terms-container,
      .warranty-container,
      .acceptance-container,
      .noteacceptance-container {
        border: none !important;
        outline: none !important;
        border-radius: 10px !important;
        padding: 12px !important;
        background: #fff !important;
        box-shadow: 0 8px 18px rgba(0,0,0,0.12) !important;
        margin-bottom: 12px !important;
  
        break-inside: auto !important;
        page-break-inside: auto !important;
        overflow: visible !important;
      }
  
      /* Text tweaks */
      .companyinfo-container p,
      .property-container p,
      .scope-container p,
      .description-container p,
      .total-container p,
      .terms-container p,
      .warranty-container p,
      .noteacceptance-container p,
      .acceptance-container p {
        margin: 4px 0 !important;
        font-size: 10.75pt !important;
        line-height: 1.35 !important;
        word-break: break-word !important;
        overflow-wrap: anywhere !important;
      }
  
      .total-text {
        white-space: pre-line !important;
        line-height: 1.35 !important;
        font-size: 11.25pt !important;
      }
  
      .job-bullet { display: block !important; margin-left: 10px !important; }
      .job-bold   { font-weight: 700 !important; }
      .job-italic { font-style: italic !important; }
  
      /* Images: grid that can flow naturally */
      .image-grid {
        display: grid !important;
        grid-template-columns: repeat(3, 1fr) !important;
        gap: 8px !important;
        justify-content: center !important;
      }
      .image-wrapper {
        border: none !important;
        border-radius: 8px !important;
        background: #fafafa !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        overflow: hidden !important;
        width: 100% !important;
        aspect-ratio: 1 / 1 !important;
        break-inside: avoid !important;
        page-break-inside: avoid !important;
        box-shadow: 0 6px 14px rgba(0,0,0,0.10) !important;
      }
      .proposal-image { width: 100% !important; height: 100% !important; object-fit: contain !important; }
  
      .signature-row p { font-size: 10.75pt !important; margin: 2px 0 !important; }
  
      .page-break { break-before: page !important; page-break-before: always !important; }
    </style>
  `;

    return {
      title,
      html: `
        <!doctype html>
        <html>
          <head>
            <meta charset="utf-8" />
            <title>${title}</title>
            ${printCss}
          </head>
          <body>
            ${content}
          </body>
        </html>
      `,
    };
  };

  const openPrintPreview = () => {
    const printDocument = buildPrintDocument();
    if (!printDocument) return;

    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.setAttribute("aria-hidden", "true");
    document.body.appendChild(iframe);

    const frameWindow = iframe.contentWindow || iframe;
    const frameDocument = frameWindow.document || iframe.contentDocument;

    frameDocument.open();
    frameDocument.write(printDocument.html);
    frameDocument.close();

    const doPrint = () => {
      try { frameWindow.focus(); } catch {}
      try { frameWindow.print(); } catch {}
      setTimeout(() => {
        try { document.body.removeChild(iframe); } catch {}
      }, 1000);
    };

    if (frameDocument.readyState === "complete") {
      setTimeout(doPrint, 150);
    } else {
      iframe.onload = () => setTimeout(doPrint, 150);
    }
  };

  const handlePrint = async () => {
    if (!selectedProposal) return;

    // 1) Open the browser print dialog using the exact same rendered proposal HTML.
    openPrintPreview();

    // 2) Save that same print document to Storage for emailing.
    try {
      const printDocument = buildPrintDocument();
      if (!printDocument) return;

      const now = new Date();
      const timestamp = now.toISOString();
      const proposalPDFname = `${printDocument.title}.pdf`;
      const storagePath = `proposals/${selectedProposal.id}/pdfs/${proposalPDFname}`;

      const holder = document.createElement("div");
      holder.style.position = "fixed";
      holder.style.left = "-10000px";
      holder.style.top = "0";
      holder.style.width = "7.5in";
      holder.innerHTML = printDocument.html;
      document.body.appendChild(holder);

      const target = holder.querySelector(".printable-content") || holder.querySelector("#printableArea");
      if (!target) {
        document.body.removeChild(holder);
        throw new Error("Printable proposal content was not found.");
      }

      // html2pdf does not honor @media print the same way the browser print dialog does.
      // Remove the app-only controls before creating the saved/email PDF so the PDF ends at Warranty.
      target.querySelectorAll(".no-print").forEach((el) => el.remove());

      const pdfBlob = await html2pdf()
        .set({
          margin: [0.5, 0.5, 0.5, 0.5],
          filename: proposalPDFname,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: "#ffffff",
          },
          jsPDF: {
            unit: "in",
            format: "letter",
            orientation: "portrait",
          },
          pagebreak: {
            mode: ["css", "legacy"],
            avoid: [
              ".image-wrapper",
              ".image-container",
              ".terms-container",
              ".warranty-container",
              ".acceptance-container",
              ".total-container",
              ".scope-container",
            ],
          },
        })
        .from(target)
        .outputPdf("blob");

      document.body.removeChild(holder);

      const storage = getStorage();
      const pdfRef = ref(storage, storagePath);

      await uploadBytes(pdfRef, pdfBlob, {
        contentType: "application/pdf",
      });

      const downloadURL = await getDownloadURL(pdfRef);
      setPdfDownloadUrl(downloadURL);

      const proposalRef = doc(db, "proposals", selectedProposal.id);

      const pdfFields = {
        latestProposalPdfUrl: downloadURL,
        latestProposalPdfName: proposalPDFname,
        latestProposalPdfStoragePath: storagePath,
        latestProposalPdfGeneratedAt: timestamp,
      };

      await updateDoc(proposalRef, pdfFields);

      // Keep the open Details popup and list row current so Click to Send uses this PDF immediately.
      setSelectedProposal((prev) =>
        prev?.id === selectedProposal.id ? { ...prev, ...pdfFields } : prev
      );
      setProposals((prev) =>
        prev.map((p) =>
          p.id === selectedProposal.id ? { ...p, ...pdfFields } : p
        )
      );

      await addDoc(collection(db, `proposals/${selectedProposal.id}/pdfs`), {
        proposalPDF: downloadURL,
        proposalPDFname,
        storagePath,
        timestamp,
        dateTimeString: now.toLocaleString(),
      });

      await matchAndAddPdfToJob(
        selectedProposal.siteName,
        selectedProposal.scope,
        downloadURL,
        proposalPDFname,
        timestamp,
        now.toLocaleString()
      );
    } catch (err) {
      console.error("Error generating print-version PDF:", err);
      alert("Could not save the print-version PDF for emailing.");
    }
  };
  
  
  const matchAndAddPdfToJob = async (siteName, scope, pdfURL, pdfName, timestamp, dateTimeString) => {
    try {
      // Fetch jobs collection
      const jobsRef = collection(db, "jobs");
      const querySnapshot = await getDocs(jobsRef);
  
      // Find a job that matches the siteName and scope
      const matchingJobDoc = querySnapshot.docs.find(
        (doc) => doc.data().siteName === siteName && doc.data().scope === scope
      );
  
      if (matchingJobDoc) {
        const jobId = matchingJobDoc.id;
        const jobRef = doc(db, "jobs", jobId);
  
        // Get existing PDFs array or initialize a new one
        const existingJobData = matchingJobDoc.data();
        const existingPdfs = existingJobData.proposalPdfs || [];
  
        // Add the new PDF to the array
        const updatedPdfs = [...existingPdfs, { pdfURL, pdfName, timestamp, dateTimeString }];
  
        // Update Firestore with the new PDF
        await updateDoc(jobRef, { proposalPdfs: updatedPdfs });
  
        console.log(`PDF successfully added to job with ID: ${jobId}`);
      } else {
        console.warn(`No matching job found for Site: ${siteName}, Scope: ${scope}`);
      }
    } catch (error) {
      console.error("Error updating job with PDF:", error);
    }
  };
  
const getImageData = async (url) => {
  const response = await fetch(url);
  const blob = await response.blob();
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
};

const generatePDF = async (proposal, fontSize = 12, termsFontSize = 12, termsFontStyle = 'normal') => {
  const doc = new jsPDF();
  doc.setFont("helvetica", "normal"); // ✅ Arial-style font
  doc.setFontSize(fontSize);
  
  // ✅ no outer border
  
  let yOffset = 18;
  
  if (proposal.logo) {
    const logoImage = await getImageData(proposal.logo);
    doc.addImage(logoImage, "JPEG", 155, 10, 40, 16);
  }
  
  // company info under logo (right-aligned)
  doc.setFont("helvetica", "bold");
  doc.text(proposal.companyName || "", 195, 32, { align: "right", maxWidth: 90 });
  
  doc.setFont("helvetica", "normal");
  doc.text(proposal.companyAddress || "", 195, 39, { align: "right", maxWidth: 90 });
  doc.text(proposal.companyCityStateZip || "", 195, 46, { align: "right", maxWidth: 90 });
  
  yOffset = 60;

    // Company container
    doc.setFillColor(255, 255, 255);
    const companyHeight = 10 + (proposal.companyName ? 10 : 0) + (proposal.companyAddress ? 10 : 0) + (proposal.companyCityStateZip ? 10 : 0);
    doc.text("Company:", 20, yOffset + 10);
    if (proposal.companyName) doc.text(proposal.companyName, 105, yOffset + 20, { maxWidth: 170, align: 'center' });
    if (proposal.companyAddress) doc.text(proposal.companyAddress, 105, yOffset + 30, { maxWidth: 170, align: 'center' });
    if (proposal.companyCityStateZip) doc.text(proposal.companyCityStateZip, 105, yOffset + 40, { maxWidth: 170, align: 'center' });
    yOffset += companyHeight + 10;

    // Site container
    const siteHeight = 10 + (proposal.siteName ? 10 : 0) + (proposal.management ? 10 : 0) + (proposal.managerName ? 10 : 0) + (proposal.address ? 10 : 0);
    doc.text("Site:", 20, yOffset + 10);
    if (proposal.siteName) doc.text(`Site: ${proposal.siteName}`, 20, yOffset + 20, { maxWidth: 170 });
    if (proposal.management) doc.text(`Management: ${proposal.management}`, 20, yOffset + 30, { maxWidth: 170 });
    if (proposal.managerName) doc.text(`Manager: ${proposal.managerName}`, 20, yOffset + 40, { maxWidth: 170 });
    if (proposal.address) doc.text(`Address: ${proposal.address}`, 20, yOffset + 50, { maxWidth: 170 });
    yOffset += siteHeight + 10;

    // Scope container
    const scopeHeight = 20;
    doc.text("Scope:", 20, yOffset + 10);
    doc.text(proposal.scope || '', 20, yOffset + 20, { maxWidth: 170 });
    yOffset += scopeHeight + 10;

    // Job Description container
    const jobDescriptionLines = (proposal.jobDescription || '').split('\n');
    const jobDescriptionText = jobDescriptionLines.map(line => `• ${line}`).join('\n');
    const jobDescriptionHeight = jobDescriptionLines.length * 10 + 10; // Adjust height based on line count

    doc.text("Job Description:", 20, yOffset + 10);
    doc.text(jobDescriptionText, 20, yOffset + 20, { maxWidth: 170, align: 'left' });

    yOffset += jobDescriptionHeight + 10; // Adjust for next section

    // Total Price container
    const totalPriceHeight = 20;
    doc.text("Total Price:", 20, yOffset + 10);
    doc.text(`$${proposal.price || ''}`, 20, yOffset + 20, { maxWidth: 170 });
    yOffset += totalPriceHeight + 10;

    // Options container
    if (proposal.options && proposal.options.length > 0) {
        doc.addPage();
        yOffset = 10;
        const optionsHeight = 20 + proposal.options.length * 10;
        doc.setFillColor(255, 255, 255);
        doc.text("Options:", 20, yOffset + 10);
        proposal.options.forEach((option, index) => {
            doc.text(`Option ${String.fromCharCode(65 + index)} Total: ${option}`, 20, yOffset + 20 + index * 10, { maxWidth: 170 });
        });
        yOffset += optionsHeight + 10;
    }

    // Additional Note container
    doc.addPage();
    yOffset = 10;
    const additionalNoteHeight = 30;
    doc.setFillColor(255, 255, 255);
    doc.text("Additional Note:", 20, yOffset + 10);
    doc.text(proposal.additionalNote || '', 20, yOffset + 20, { maxWidth: 170 });
    yOffset += additionalNoteHeight + 10;

    // Image Upload container
    if (proposal.images && proposal.images.length > 0) {
        doc.addPage();
        yOffset = 10;
        const imagesHeight = 20 + Math.ceil(proposal.images.length / 3) * 110; // Adjust height based on image count
        doc.setFillColor(255, 255, 255);
        doc.text("Images:", 20, yOffset + 10);
        for (const [index, imageUrl] of proposal.images.entries()) {
            const imageData = await getImageData(imageUrl);
            const x = 20 + (index % 3) * 60; // Adjust x position for 3 images per row
            const y = yOffset + 20 + Math.floor(index / 3) * 100; // Adjust y position for each row
            doc.addImage(imageData, 'JPEG', x, y, 50, 50); // Adjust
        }
        yOffset += imagesHeight + 10;
    }

    // Add page break for terms
    doc.addPage();
    yOffset = 10;
    
    // Terms & Conditions
    doc.setFontSize(termsFontSize);
    doc.setFont("helvetica", termsFontStyle);
    doc.text("Terms & Conditions:", 15, yOffset + 10);

    // Handle line breaks in terms
    const termsLines = (proposal.terms || '').split('\n');
    yOffset += 20;
    termsLines.forEach(line => {
        doc.text(line, 15, yOffset, { maxWidth: 180 });
        yOffset += 10;
    });
    
    // Save PDF
    const filename = `${proposal.siteName} ${proposal.scope}`.replace(/[/\\?%*:|"<>]/g, '') + '.pdf';
doc.save(filename);
};

const generateAndUploadPDF = async () => {
    const doc = new jsPDF();
    doc.text("This is a dynamically created PDF stored in Firebase!", 10, 10);
    const pdfBlob = doc.output("blob");

    const storage = getStorage();
    const pdfRef = ref(storage, `generated_pdfs/my_generated_pdf.pdf`);

    try {
      const snapshot = await uploadBytes(pdfRef, pdfBlob);
      const downloadURL = await getDownloadURL(snapshot.ref);
      console.log("PDF uploaded successfully:", downloadURL);

      document.getElementById("pdfLink").href = downloadURL;
      document.getElementById("pdfLink").innerText = "Download Generated PDF";
      document.getElementById("pdfViewer").src = downloadURL;
    } catch (error) {
      console.error("Error uploading PDF:", error);
    }
  };

const handleAddProposalClick = () => {
  const ppd = companies.find(c => c.companyName === PPD_NAME);
  setNewProposal({
    companyName: PPD_NAME,
    companyAddress: ppd?.companyAddress || "",
    companyCityStateZip: ppd ? `${ppd.companyCity}, ${ppd.companyState} ${ppd.companyZip}` : "",
    siteName: "",
  management: "",
  managerName: "",
  managerEmail: "",
  clientEmail: "",
  accountingEmail: "",
  address: "",
  cityStateZip: "",
  serviceType: "",
  scope: "",
  jobDescription: "",
  price: "",
    proposalTotal: "",
  initialAmount: "",
  feeSchedule: "Monthly",
  submittedDate: "",
  submittedBy: "",
  images: [],
  terms: "",
  warranty: "",
  });
  // ensure the PPD logo is selected
  setSelectedLogo(logoOptions[0]);
  setIsEditing(true);
};


const handleCloseDetails = () => {
  setSelectedProposal(null);
};

const handleDeleteProposalDirect = async (proposalId) => {
  try {
    const proposalRef = doc(db, "proposals", proposalId);
    await deleteDoc(proposalRef);
    setProposals(prev => prev.filter(p => p.id !== proposalId));
  } catch (error) {
    console.error("Error deleting proposal:", error);
    alert("Failed to delete proposal.");
  }
};

const handleMarkCompleted = async (proposal) => {
  try {
    const proposalRef = doc(db, "proposals", proposal.id);

    await updateDoc(proposalRef, {
      status: "Completed"
    });

    // update UI immediately
    setProposals(prev =>
      prev.filter(p => p.id !== proposal.id) // remove from Not Sent list
    );

    alert("Proposal marked as Completed.");
  } catch (error) {
    console.error("Error marking proposal as Completed:", error);
    alert("Could not update status.");
  }
};

const handleAddToSentFolder = async (proposal) => {
  const confirmed = window.confirm(
    "Move this proposal to the Sent folder without emailing it?"
  );

  if (!confirmed) return;

  try {
    const proposalRef = doc(db, "proposals", proposal.id);

    await updateDoc(proposalRef, {
      status: "Sent",
      hasBeenSent: true,
      manuallyMarkedSent: true,
      manuallyMarkedSentAt: new Date().toISOString(),
    });

    setProposals((prev) => prev.filter((p) => p.id !== proposal.id));

    if (selectedProposal?.id === proposal.id) {
      setSelectedProposal(null);
    }

    alert("Proposal added to the Sent folder without sending an email.");
  } catch (error) {
    console.error("Error adding proposal to Sent folder:", error);
    alert("Could not move the proposal to the Sent folder.");
  }
};

const handleMarkNotApproved = async (proposal) => {
  const confirmed = window.confirm("Mark this proposal as Not Approved?");
  if (!confirmed) return;

  try {
    const proposalRef = doc(db, "proposals", proposal.id);
    await updateDoc(proposalRef, {
      status: "Not Approved",
      notApprovedAt: new Date().toISOString(),
      hasBeenSent: proposal.hasBeenSent || false,
    });

    setProposals((prev) => prev.filter((p) => p.id !== proposal.id));

    if (selectedProposal?.id === proposal.id) {
      setSelectedProposal(null);
    }
  } catch (error) {
    console.error("Error marking proposal not approved:", error);
    alert("Failed to mark proposal Not Approved.");
  }
};

const handleToggleStatus = async (proposal, type) => {
  const proposalRef = doc(db, "proposals", proposal.id);
  let newStatus = proposal.status;
  let hasBeenSent = proposal.hasBeenSent || false;

  if (type === "sent") {
    if (proposal.status === "Not Sent" || !proposal.hasBeenSent) {
      // Saved PDF required, then sendProposalEmail
      // emails the client from the Priority mailbox and marks it Sent.
      const patch = await sendPPDProposalEmail(proposal, {
        onStart: (id) => setSendingProposalId(id),
        onFinish: () => setSendingProposalId(null),
      });
      if (!patch) return;

      if (patch.status === "Not Sent" || patch.status === "Sent") {
        // This page lists Not Sent proposals only.
        setProposals((prev) => prev.filter((p) => p.id !== proposal.id));
        if (selectedProposal?.id === proposal.id) {
          setSelectedProposal(null);
        }
      } else {
        setProposals((prev) => prev.map((p) => (p.id === proposal.id ? { ...p, ...patch } : p)));
      }
      return;
    } else if (proposal.status === "Sent") {
      newStatus = "Not Sent";
      hasBeenSent = false;
    } else if (proposal.status === "Approved") {
      hasBeenSent = !hasBeenSent;
    }
  } else if (type === "approval") {
    newStatus = "Approved";
    hasBeenSent = true;
  }

  try {
    await updateDoc(proposalRef, {
      status: newStatus,
      hasBeenSent,
    });

    setProposals((prev) =>
      prev.map((p) =>
        p.id === proposal.id ? { ...p, status: newStatus, hasBeenSent } : p
      )
    );

    if (proposal.status !== "Approved" && newStatus === "Approved") {
      const freshSnap = await getDoc(proposalRef);
      const fresh = freshSnap.exists() ? freshSnap.data() : proposal;
      await upsertJobFromProposal({ id: proposal.id, ...fresh });
    }
  } catch (error) {
    console.error("Error updating status:", error);
    alert("Failed to update proposal status.");
  }
};

const getNextWorkOrder = async (prefix) => {
  const counterRef = doc(db, "counters", `${prefix}_jobs`);
  let nextNumber;

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(counterRef);

    if (!snap.exists()) {
      // first time: start at 10001
      nextNumber = 10001;
      tx.set(counterRef, { lastNumber: nextNumber, updatedAt: serverTimestamp() });
    } else {
      const data = snap.data() || {};
      const last = Number(data.lastNumber);
      const safeLast = Number.isFinite(last) ? last : 10000;
      nextNumber = safeLast + 1;
      tx.update(counterRef, { lastNumber: nextNumber, updatedAt: serverTimestamp() });
    }
  });

  return `${prefix}-${nextNumber}`;
};

const handleSortBySite = () => {
  const sorted = [...proposals].sort((a, b) => {
    const nameA = a.siteName?.toLowerCase() || "";
    const nameB = b.siteName?.toLowerCase() || "";

    if (sortOrder === "asc") {
      return nameA.localeCompare(nameB);
    } else {
      return nameB.localeCompare(nameA);
    }
  });

  setProposals(sorted);
  setSortOrder(sortOrder === "asc" ? "desc" : "asc");
};

const upsertJobFromProposal = async (proposal) => {
  try {
    const jobsRef = collection(db, "jobs");
    const q = query(jobsRef, where("proposalId", "==", proposal.id));
    const existing = await getDocs(q);

    const images =
      proposal.imageURLs && proposal.imageURLs.length
        ? proposal.imageURLs
        : proposal.images || [];

    if (!existing.empty) {
      const jobDoc = existing.docs[0];
      await updateDoc(doc(db, "jobs", jobDoc.id), { status: "Active", estimatedTotal: Number(proposal.proposalTotal || proposal.price || 0) });
      await addJobTotal({
        jobId: jobDoc.id,
        wo: jobDoc.data()?.wo || proposal.wo || jobDoc.id,
        proposalId: proposal.id,
        total: proposal.proposalTotal || proposal.price || 0,
        source: "approved-proposal",
      });
      return jobDoc.id;
    }

    // Try to get a WO, but don't fail the whole op if it errors
    let wo = "";
    try {
      wo = await getNextWorkOrder("PPD");
      await updateDoc(doc(db, "proposals", proposal.id), { wo });
      setProposals(prev => prev.map(p => p.id === proposal.id ? { ...p, wo } : p));
    } catch (e) {
      console.warn("WO generation failed; creating job without WO:", e);
    }

    const ts = new Date();
    const jobData = {
      proposalId: proposal.id,
      companyName: proposal.companyName || "",
      companyAddress: proposal.companyAddress || "",
      companyCityStateZip: proposal.companyCityStateZip || "",
      siteName: proposal.siteName || "",
      management: proposal.management || "",
      managerName: proposal.managerName || "",
      address: proposal.address || "",
      cityStateZip: proposal.cityStateZip || "",
      serviceType: proposal.serviceType || "",
      scope: proposal.scope || "",
      jobDescription: proposal.jobDescription || "",
      price: proposal.price || "",
      proposalTotal: proposal.proposalTotal || proposal.price || "",
      initialAmount: proposal.initialAmount || "",
      feeSchedule: proposal.feeSchedule || "Monthly",
      recurringInvoiceAmount: Number(proposal.proposalTotal || proposal.price || 0),
      recurringFeeSchedule: proposal.feeSchedule || "Monthly",
      estimatedTotal: Number(proposal.proposalTotal || proposal.price || 0),
      submittedDate: proposal.submittedDate || "",
      submittedBy: proposal.submittedBy || "",
      terms: proposal.terms || "",
      warrantyName: proposal.warrantyName || "",
      warranty: proposal.warranty || "",
      images,
      wo,
      status: "Active",
      createdAt: ts.toISOString(),
    };

    const newJobRef = await addDoc(jobsRef, jobData);
    await addJobTotal({
      jobId: newJobRef.id,
      wo,
      proposalId: proposal.id,
      total: proposal.proposalTotal || proposal.price || 0,
      source: "approved-proposal",
    });
    return newJobRef.id;
  } catch (err) {
    console.error("upsertJobFromProposal error:", err);
    alert("Failed to create/activate job. Check console for details.");
    return null;
  }
};

const renderJobDescriptionLines = (text) => {
  if (!text) return null;

  return text.split("\n").map((line, index) => {
    const rawLine = line || "";
    const trimmed = rawLine.trim();

    if (trimmed === "") {
      return <br key={index} />;
    }

    if (/^\(\([bB]\)\)/.test(trimmed)) {
      return (
        <span key={index} className="job-bold">
          <strong>{trimmed.replace(/^\(\([bB]\)\)/, "").trim()}</strong>
          <br />
        </span>
      );
    }

    if (/^\(\([iI]\)\)/.test(trimmed)) {
      return (
        <span key={index} className="job-italic">
          <i>{trimmed.replace(/^\(\([iI]\)\)/, "").trim()}</i>
          <br />
        </span>
      );
    }

    if (/^\(\([rR]\)\)/.test(trimmed)) {
      return (
        <span key={index}>
          {trimmed.replace(/^\(\([rR]\)\)/, "").trim()}
          <br />
        </span>
      );
    }

    if (trimmed.startsWith("((-))")) {
      return (
        <span key={index} className="job-bullet">
          • {trimmed.replace("((-))", "").trim()}
          <br />
        </span>
      );
    }

    return (
      <span key={index} className="job-bullet">
        • {rawLine}
        <br />
      </span>
    );
  });
};

return (
  <div className="proposals-page">

    <div className="center-add-button">
  <button className="blue-button" onClick={handleAddProposalClick}>
    Add Proposal
  </button>
</div>
      <div style={{ display: "flex", justifyContent: "right" }}>
 
</div>
      <h2 style={{margin:"0 0 12px 0", textAlign:"center"}}>PPD Proposals</h2>
     
      {isEditing && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>{selectedProposal ? "Edit Proposal" : "Add Proposal"}</h3>
            <form className="form-container" onSubmit={handleSubmit}>
              <label>Logo:</label>
              <select name="logo" value={selectedLogo.name} onChange={handleLogoChange} required>
                {logoOptions.map(option => (
                  <option key={option.name} value={option.name}>{option.name}</option>
                ))}
              </select>
             <label>Company Name:</label>
<input
  type="text"
  name="companyName"
  value={newProposal.companyName}
  readOnly
/>


              <label>Company Address:</label>
              <input type="text" name="companyAddress" value={newProposal.companyAddress} onChange={handleInputChange} readOnly />
              <label>Company City, State, Zip:</label>
              <input type="text" name="companyCityStateZip" value={newProposal.companyCityStateZip} onChange={handleInputChange} readOnly />
              <label>Site Name:</label>
              <select name="siteName" value={newProposal.siteName} onChange={handleSiteNameChange} required>
                <option value="">Select Site Name</option>
                {sortedProperties.map(property => (
                  <option key={property.id} value={property.propertyName}>{property.propertyName}</option>
                ))}
              </select>
              <label>Management:</label>
              <input type="text" name="management" value={newProposal.management} onChange={handleInputChange} readOnly />
              <label>Manager Name:</label>
              <input type="text" name="managerName" value={newProposal.managerName} onChange={handleInputChange} readOnly />
              <label>Address:</label>
              <select name="address" value={newProposal.address} onChange={handleInputChange} required>
                <option value="">Select Address</option>
                {properties.map(property => (
                  <option key={property.id} value={property.address || property.managementAddress}>{property.address || property.managementAddress}</option>
                ))}
              </select>
              <label>City, State, Zip:</label>
<select name="cityStateZip" value={newProposal.cityStateZip} onChange={handleInputChange} required>
  <option value="">Select City, State, Zip</option>
  {properties.map(property => (
    <option
      key={property.id}
      value={`${property.city || property.managementCity}, ${property.state || property.managementState} ${property.zip || property.managementZip}`}
    >
      {`${property.city || property.managementCity}, ${property.state || property.managementState} ${property.zip || property.managementZip}`}
    </option>
  ))}
</select>

<label>Service Type:</label>
{/*
<select
  name="serviceType"
  value={newProposal.serviceType}
  onChange={handleServiceTypeChange}
>
*/}

<select
  name="serviceType"
  value={newProposal.serviceType}
  onChange={handleServiceTypeChange}
>
  <option value="">Select Service Type</option>
  {[...serviceTypeTemplates]
    .sort((a, b) => String(a.serviceType || "").localeCompare(String(b.serviceType || "")))
    .map((template) => {
      const type = template.serviceType;
      return (
        <option key={template.id || type} value={type}>
          {type}
        </option>
      );
    })}
</select>

<label>Scope:</label>
<input
  type="text"
  name="scope"
  value={newProposal.scope}
  onChange={handleInputChange}
  required
/>

{/* ✅ ===== Dynamic Proposal Dropdowns (from ProposalsPPDDropdownsAdmin) ===== */}
<div style={{ fontSize: 12, color: "gray", marginBottom: 10 }}>
  Loaded dropdowns: {proposalDropdowns.length}
</div>

{proposalDropdowns.filter((dropdown) => !isLegacyServiceDropdown(dropdown)).map((dropdown) => (
  <div key={dropdown.id}>
    <label>{dropdown.label}:</label>

    <select
      value=""
      onChange={(e) => {
        handleDropdownChange(dropdown.fieldName, e.target.value, dropdown.label);
        e.target.value = ""; // auto-reset
      }}
    >
      <option value="">Select {dropdown.label}</option>

      {(dropdown.options || []).map((opt, i) => (
  <option key={i} value={JSON.stringify(opt)}>
    {opt.title || opt.label}
  </option>
))}

    </select>
  </div>
))}

{/* ✅ Sub-options popup */}
{activeSubOptions && (
  <div className="sub-option-box">
    <h4>{activeSubOptions.label}</h4>

    {activeSubOptions.options.map((sub, i) => (
      <button
        key={i}
        type="button"
        onClick={() => {
          addToJobDescription(activeSubOptions.field, sub);

          setActiveSubOptions(null);
        }}
      >
        {sub}
      </button>
    ))}

    <button
      type="button"
      className="red-button"
      onClick={() => setActiveSubOptions(null)}
    >
      Cancel
    </button>

    <button
      type="button"
      className="clear-scope-button"
      onClick={() =>
        setNewProposal((prev) => ({
          ...prev,
          jobDescription: "",
          dropdownValues: {},
        }))
      }
    >
      🗑 Clear Description
    </button>
  </div>
)}

<label>
  Job Description: (Notes: Add ((b)) for bold lines, add ((i)) for italic lines. Bold and italic lines, and spaces have no bullets.)
</label>
<textarea
  className="proposal-textarea"
  name="jobDescription"
  value={newProposal.jobDescription}
  onChange={handleInputChange}
/>


              <label>Job Description Preview:</label>
              <div className="job-description-preview">
  {renderJobDescriptionLines(newProposal.jobDescription)}
</div>

              <label>Price:</label>
              <textarea
                name="price"
                value={newProposal.price}
                onChange={handleInputChange}
                rows={3}
                required
              ></textarea>

              <label>Proposal Total (Internal Only - does not show on proposal):</label>
              <input
                type="number"
                name="proposalTotal"
                value={newProposal.proposalTotal || ""}
                onChange={handleInputChange}
                onBlur={(e) => persistProposalPricingOverride("amount", e.target.value)}
                placeholder="Internal proposal total"
              />
              <label>Internal Initial Amount (Internal Only - does not show on proposal):</label>
              <input
                type="number"
                name="initialAmount"
                value={newProposal.initialAmount || ""}
                onChange={handleInputChange}
                placeholder="Initial fee to start the job"
              />
              <label>Submitted Date:</label>
              <input type="date" name="submittedDate" value={newProposal.submittedDate} onChange={handleInputChange} />
              <label>Submitted By:</label>
              <select
  name="submittedBy"
  value={newProposal.submittedBy}
  onChange={handleInputChange}
>
  <option value="">Select User</option>
  {users.map(user => {
    const initials = `${(user.firstName || "").charAt(0)} ${(user.lastName || "").charAt(0)}`.trim();
    return (
      <option key={user.id} value={initials}>
        {initials}
      </option>
    );
  })}
</select>

              <label>Image Display:</label>
              <input type="file" name="imageDisplay" onChange={handleFileChange} multiple />
              <label>Terms:</label>
<select
  name="terms"
  value={newProposal.terms}
  onChange={handleInputChange}
>
  <option value="">Select Terms</option>
  {termsOptions.map(term => (
    <option key={term.name} value={term.content}>
      {term.name}
    </option>
  ))}
</select>

<label>Warranty:</label>
<select
  name="warrantyName"
  value={newProposal.warrantyName}
  onChange={handleWarrantyChange}
>
  <option value="">Select Warranty</option>
  {warrantyOptions.map(opt => (
    <option key={opt.name} value={opt.name}>
      {opt.name}
    </option>
  ))}
</select>

              <div className="modal-buttons">
                <button type="submit" className="blue-button">Save</button>
                <button type="button" className="red-button" onClick={() => setIsEditing(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

<div className="table-container" style={{ maxHeight: "70vh", overflow: "auto" }}>
  <div className="table-scroll" style={{ maxHeight: "70vh", overflow: "auto" }}>
    <table>

          <thead>
            <tr>
            <th style={{ whiteSpace: "nowrap" }}>
  Site Name
  <button 
    onClick={handleSortBySite}
    style={{
      marginLeft: "6px",
      padding: "2px 6px",
      fontSize: "11px",
      cursor: "pointer"
    }}
  >
    {sortOrder === "asc" ? "▲" : "▼"}
  </button>
</th>
              <th>Scope</th>
              <th>Submitted Date</th>
              <th>User</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
          {proposals.map(proposal => (
  <tr
  key={proposal.id}
  style={{
    backgroundColor:
      proposal.status === "Approved"
        ? "#d4edda" // green
        : proposal.status === "Sent"
        ? "#f8d7da" // pink
        : "transparent"
  }}
>
  <td>{proposal.siteName}</td>
  <td>{proposal.scope}</td>
  <td>{formatDateMMDDYYYY(proposal.submittedDate)}</td>
  <td>{formatInitials(proposal.submittedBy)}</td>
  <td>
  <button className="smalldetails-button" onClick={() => setSelectedProposal(proposal)}>Details</button>
  <button className="smalldelete-button" onClick={() => handleDeleteProposalDirect(proposal.id)} style={{ marginLeft: "8px" }}>Delete</button>
  <button
    className={`smallstatus-button ${proposal.status === "Approved" ? "approved" : ""}`}
    onClick={() => handleToggleStatus(proposal, "approval")}
    style={{ marginLeft: "8px" }}
  >
    Approved
  </button>
  <button
    className={`smallstatus-button ${proposal.hasBeenSent ? "sent" : ""}`}
    onClick={() => handleToggleStatus(proposal, "sent")}
    disabled={sendingProposalId === proposal.id}
    style={{ marginLeft: "8px" }}
  >
    {sendingProposalId === proposal.id ? "Sending..." : proposal.hasBeenSent ? "Sent" : "Click to Send"}
  </button>
  <button
    className="smallstatus-button"
    onClick={() => handleAddToSentFolder(proposal)}
    style={{ marginLeft: "8px" }}
  >
    Add to Sent Folder
  </button>
  <button
    className="smallstatus-button not-approved"
    onClick={() => handleMarkNotApproved(proposal)}
    style={{ marginLeft: "8px" }}
  >
    Not Approved
  </button>
  <button
  className="smallcomplete-button"
  onClick={() => handleMarkCompleted(proposal)}
  style={{ marginLeft: "8px" }}
>
  Completed
</button>
</td>

</tr>

))}


          </tbody>
          </table>
  </div>
</div>


      {selectedProposal && (
        <div className="modal full-size-modal"> {/* Add full-size-modal class */}
          {isEditing ? (
            <div className="form-container shrink-form"> {/* Add shrink-form class */}
             <label>Company Name:</label>
<input
  type="text"
  name="companyName"
  value={newProposal.companyName}
  readOnly
/>


              <label>Company Address:</label>
              <input type="text" name="companyAddress" value={newProposal.companyAddress} onChange={handleInputChange} readOnly />
              <label>Company City, State, Zip:</label>
              <input type="text" name="companyCityStateZip" value={newProposal.companyCityStateZip} onChange={handleInputChange} readOnly />
              <label>Site Name:</label>
              <select name="siteName" value={newProposal.siteName} onChange={handleSiteNameChange} required>
                <option value="">Select Site Name</option>
                {sortedProperties.map(property => (
                  <option key={property.id} value={property.propertyName}>{property.propertyName}</option>
                ))}
              </select>
              <label>Management:</label>
              <input type="text" name="management" value={newProposal.management} onChange={handleInputChange} readOnly />
              <label>Manager Name:</label>
              <input type="text" name="managerName" value={newProposal.managerName} onChange={handleInputChange} readOnly />
              <label>Address:</label>
              <select name="address" value={newProposal.address} onChange={handleInputChange} required>
                <option value="">Select Address</option>
                {properties.map(property => (
                  <option key={property.id} value={property.address || property.managementAddress}>{property.address || property.managementAddress}</option>
                ))}
              </select>
              <label>City, State, Zip:</label>
              <select name="cityStateZip" value={newProposal.cityStateZip} onChange={handleInputChange} required>
                <option value="">Select City, State, Zip</option>
                {properties.map(property => (
                  <option key={property.id} value={`${property.city || property.managementCity}, ${property.state || property.managementState} ${property.zip || property.managementZip}`}>{`${property.city || property.managementCity}, ${property.state || property.managementState} ${property.zip || property.managementZip}`}</option>
                ))}
              </select>
              <label>Service Type:</label>

              {/*
<select
  name="serviceType"
  value={newProposal.serviceType}
  onChange={handleServiceTypeChange}
>
*/}
<select
  name="serviceType"
  value={newProposal.serviceType}
  onChange={handleServiceTypeChange}
>
  <option value="">Select Service Type</option>
  {[...serviceTypeTemplates]
    .sort((a, b) => String(a.serviceType || "").localeCompare(String(b.serviceType || "")))
    .map((template) => {
      const type = template.serviceType;
      return (
        <option key={template.id || type} value={type}>
          {type}
        </option>
      );
    })}
</select>
              <label>Scope:</label>
              <input type="text" name="scope" value={newProposal.scope} onChange={handleInputChange} required />
              <label>Job Description:</label>
<textarea
  name="jobDescription"
  value={newProposal.jobDescription}
  onChange={handleInputChange}
  rows={6}
/>

<label>Job Description Preview:</label>
<div className="job-description-preview">
  {renderJobDescriptionLines(newProposal.jobDescription)}
</div>

              <label>Price:</label>
              <textarea
  className="proposal-textarea proposal-price"
  name="price"
  value={newProposal.price}
  onChange={handleInputChange}
  rows={3}
></textarea>

              <label>Proposal Total (Internal Only - does not show on proposal):</label>
              <input
                type="number"
                name="proposalTotal"
                value={newProposal.proposalTotal || ""}
                onChange={handleInputChange}
                onBlur={(e) => persistProposalPricingOverride("amount", e.target.value)}
                placeholder="Internal proposal total"
              />

              <label>Internal Initial Amount (Internal Only - does not show on proposal):</label>
              <input
                type="number"
                name="initialAmount"
                value={newProposal.initialAmount || ""}
                onChange={handleInputChange}
                placeholder="Initial fee to start the job"
              />
              <label>Submitted Date:</label>
              <input type="date" name="submittedDate" value={newProposal.submittedDate} onChange={handleInputChange} />
              <label>Submitted By:</label>
              <select
  name="submittedBy"
  value={newProposal.submittedBy}
  onChange={handleInputChange}
>
  <option value="">Select User</option>
  {users.map(user => {
    const initials = `${(user.firstName || "").charAt(0)} ${(user.lastName || "").charAt(0)}`.trim();
    return (
      <option key={user.id} value={initials}>
        {initials}
      </option>
    );
  })}
</select>

              <label>Image Display:</label>
              <input type="file" name="imageDisplay" onChange={handleFileChange} multiple />
              <label>Terms:</label>
              <select name="terms" value={newProposal.terms} onChange={handleInputChange} required>
                <option value="">Select Terms</option>
                {termsOptions.map(term => (
                  <option key={term.name} value={term.content}>{term.name}</option>
                ))}
              </select>
              <label>Warranty:</label>
              <select
                name="warrantyName"
                value={newProposal.warrantyName}
                onChange={handleWarrantyChange}
                required
              >
                <option value="">Select Warranty</option>
                {warrantyOptions.map(opt => (
                  <option key={opt.name} value={opt.name}>{opt.name}</option>
                ))}
              </select>
              <div className="modal-buttons">
                <button onClick={handleEditProposal} className="blue-button">Save</button>
                <button onClick={handleCancel} className="red-button">Cancel</button>
              </div>
            </div>
          ) : (
            <div id="printableArea" className="printable-content full-size-content"> {/* Add full-size-content class */}
             <div className="page-border">
  <div className="proposal-details-layout">
    {/* ✅ NEW: top row — property left, logo top-right, company info under logo */}
<div className="pdf-top-row">
  <div className="property-container bordered-box">
    <p><strong>{selectedProposal.siteName}</strong></p>
    <p><strong>{selectedProposal.management}</strong></p>
    <p><strong>{selectedProposal.address}</strong></p>
    <p><strong>{selectedProposal.cityStateZip}</strong></p>
  </div>

  <div className="pdf-header">
    <div className="companylogo-container">
      <img
        src={selectedLogo.url}
        alt="Company Logo"
        className={`resizable-logo ${selectedLogo.alignment}`}
      />
    </div>

    <div className="companyinfo-container bordered-box common-shadow">
      <p><strong>{selectedProposal.companyName}</strong></p>
      <p><strong>{selectedProposal.companyAddress}</strong></p>
      <p><strong>{selectedProposal.companyCityStateZip}</strong></p>
    </div>
  </div>
</div>

                  {/* .scope-container */}
                  <div className="scope-container bordered-box">
                    <p><strong>Scope:</strong> {selectedProposal.scope}</p>
                  </div>



                  {/* .description-container */}
                  <div className="description-container bordered-box">
                    <p><strong>Job Description:</strong></p>
                    <p className="job-description">
  {renderJobDescriptionLines(selectedProposal.jobDescription)}
</p>
                  </div>

                  {/* .total-container */}
                  <div className="total-container bordered-box common-shadow">
  <p className="total-text"><strong>{selectedProposal.price}</strong></p>
</div>

                  {/* .noteacceptance-container */}
                  <div className="noteacceptance-container centered-text">
  <p>
    <strong>
      NOTE: Pest conditions can vary due to sanitation practices, moisture issues, neighboring properties, structural gaps, and other factors beyond our control. If additional infestations, inaccessible areas, or treatment needs are identified outside the agreed scope of service, the client will be notified and written approval will be required prior to performing additional work. Such services may incur additional fees.
    </strong>
  </p>
</div>

                  {/* .acceptance-container */}
<div className="acceptance-container centered-text">
  <p>
    <strong>Prepared By:</strong> {formatInitials(selectedProposal.submittedBy)}{" "}
    {formatDateMMDDYYYY(selectedProposal.submittedDate)}
  </p>

  <p>
    <i>This proposal is valid for thirty (30) days from the date listed above and may be revoked thereafter if not accepted.</i>
  </p>

  <p>
    <i>
      Proposal Acceptance: The pricing, scope of work, and terms outlined herein are acknowledged and approved by the undersigned. Authorization is hereby granted to proceed with the work as described. Payment shall be made in accordance with the agreed terms. In the event payment is not made as required, the undersigned agrees to reimburse all reasonable costs of collection, including attorney’s fees, whether or not legal action is initiated. A monthly service charge of 5% (minimum $5.00) will be assessed on any balance outstanding beyond thirty (30) days.
    </i>
  </p>

  <div
    className="signature-row"
    style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "20px" }}
  >
    <p><strong>Authorized Signature: ______________________________</strong></p>
    <p><strong>Date: ____________________</strong></p>
  </div>
</div>

                  {/* .image-container */}
                  {/* Only render image container if there are image URLs */}
{getProposalImageUrls(selectedProposal).length > 0 && (
  <div className="image-container bordered-box">
    <div className="image-grid">
      {getProposalImageUrls(selectedProposal).map((imageUrl, index) => (
        <div key={index} className="image-wrapper">
          <img
            src={imageUrl}
            alt={`Uploaded ${index + 1}`}
            className="proposal-image"
            crossOrigin="anonymous"
          />
        </div>
      ))}
    </div>
  </div>
)}


                  {/* Add a page break before .terms-container */}

                  {/* .terms-container */}
                  <div className="terms-container bordered-box">
                    <p><strong>Terms and Conditions:</strong> {selectedProposal.terms}</p>
                  </div>

                  {/* .warranty-container */}
                  {selectedProposal.warrantyName !== "No Warranty" && selectedProposal.warranty && (
  <>
    <div className="warranty-container bordered-box">
      <p><strong>Warranty:</strong> {selectedProposal.warrantyName}</p>
    </div>
    <div className="warranty-container bordered-box">
      {selectedProposal.warranty.split("\n").map((line, i) => (
        <p key={i}>{line}</p>
      ))}
    </div>
  </>
)}

                </div>
              </div>



              <div className="no-print">
  <div style={{ marginBottom: "12px", fontWeight: "600" }}>
    Service Type: {selectedProposal.serviceType || "N/A"}
  </div>
  <div style={{ marginBottom: "12px", fontWeight: "600", color: "#7c2d12" }}>
    Internal Proposal Total: ${Number(selectedProposal.proposalTotal || selectedProposal.contractAmount || selectedProposal.estimatedTotal || 0).toLocaleString()}
  </div>
  <div style={{ marginBottom: "12px", fontWeight: "600", color: "#7c2d12" }}>
    Internal Initial Amount: ${Number(selectedProposal.initialAmount || 0).toLocaleString()}
  </div>

  {selectedProposal.latestProposalPdfUrl && (
    <div style={{ marginBottom: "10px", fontWeight: 600, color: "#166534" }}>
      Saved PDF ready to email.
    </div>
  )}
  {describeLastProposalEmail(selectedProposal) && (
    <div style={{ marginBottom: "10px", fontWeight: 600, color: "#1e3a8a" }}>
      {describeLastProposalEmail(selectedProposal)}
    </div>
  )}

  <div className="modal-buttons">
    <button onClick={handlePrint} className="blue-button">Print/Download</button>
    <button onClick={() => setIsEditing(true)} className="blue-button">Edit</button>
    <button onClick={() => handleToggleStatus(selectedProposal, "approval")} className="smallstatus-button">Approved</button>
    <button onClick={() => handleMarkNotApproved(selectedProposal)} className="smallstatus-button not-approved">Not Approved</button>
    <button
      onClick={() => handleToggleStatus(selectedProposal, "sent")}
      className={`smallstatus-button ${selectedProposal.hasBeenSent ? "sent" : ""}`}
      disabled={sendingProposalId === selectedProposal.id}
    >
      {sendingProposalId === selectedProposal.id ? "Sending..." : selectedProposal.hasBeenSent ? "Sent" : "Click to Send"}
    </button>
    <button onClick={() => handleAddToSentFolder(selectedProposal)} className="smallstatus-button">Add to Sent Folder</button>
    <button onClick={() => handleMarkCompleted(selectedProposal)} className="smallcomplete-button">Completed</button>
    <button onClick={() => setShowDeleteConfirm(true)} className="red-button">Delete</button>
    <button onClick={handleCloseDetails} className="green-button">Close</button>

    {pdfDownloadUrl && (
      <div style={{ marginTop: "10px" }}>
        <a
          href={pdfDownloadUrl}
          download
          target="_blank"
          rel="noopener noreferrer"
          className="blue-button"
        >
          Download PDF
        </a>
      </div>
    )}
  </div>
  <a id="pdfLink" href="#" target="_blank"></a>
  <iframe id="pdfViewer" width="100%" height="600px"></iframe>
</div>
</div>
          )}
        </div>
      )}

      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Are you sure you want to delete?</h3>
            <div className="modal-buttons">
              <button onClick={handleDeleteProposal} className="red-button">Yes</button>
              <button onClick={() => setShowDeleteConfirm(false)} className="green-button">No</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProposalsPPD;